import { useState, useEffect, useCallback, useRef } from "react";
import {
  BookOpen,
  CheckCircle2,
  Clock,
  Loader2,
  Filter,
  Layers,
  Play,
  Sparkles,
  Video,
  BookMarked,
  AlertCircle,
  RotateCcw,
  Eye,
  Target,
  Bot,
  Brain,
} from "lucide-react";
import { ApiService } from "../../../../services/api";
import { useModal } from "../../../../hooks/useModal";
import DynamicList from "../../../../components/dynamic/List";
import DeficiencyFilter, { GainStatusFilter } from "./DeficiencyFilter";
import Modal from "../../../../components/ui/Modal";

interface GroupedDeficiency {
  contentId: string;
  contentTitle: string;
  contentType: string;
  lesson: { id: string; title: string } | null;
  topic: { id: string; title: string } | null;
  gains: Array<{
    id: string;
    gainText: string;
    isResolved: boolean;
    startTime: string;
    endTime: string;
  }>;
  gainIds: string[];
  videos: Array<{
    id: string;
    title: string;
    url: string;
    startTime: string;
    endTime: string;
  }>;
  unresolvedCount: number;
  resolvedCount: number;
  createdAt: string;
}

interface LessonOption {
  id: string;
  title: string;
}

interface TopicOption {
  id: string;
  title: string;
}

export default function TopicDeficiencyView() {
  const { showAlert } = useModal();
  const [lessons, setLessons] = useState<LessonOption[]>([]);
  const [topics, setTopics] = useState<TopicOption[]>([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<string>("");
  const [selectedTopic, setSelectedTopic] = useState<string>("");
  const [filterResolved, setFilterResolved] = useState<GainStatusFilter>("unresolved");
  const [filterOpen, setFilterOpen] = useState(true);
  const [viewMode, setViewMode] = useState<"list" | "grid">("grid");
  const [refreshKey, setRefreshKey] = useState(0);

  const [gainsModalOpen, setGainsModalOpen] = useState(false);
  const [selectedGainsItem, setSelectedGainsItem] = useState<GroupedDeficiency | null>(null);
  const [planLoading, setPlanLoading] = useState<string | null>(null); // contentId
  const planPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadLessons();
  }, []);

  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        const isMobile = window.matchMedia && window.matchMedia("(max-width: 640px)").matches;
        if (isMobile) setFilterOpen(false);
      }
    } catch (_) { }
  }, []);

  const getApiPath = () => {
    const params = new URLSearchParams();
    if (filterResolved === "resolved") params.append("isResolved", "true");
    else if (filterResolved === "unresolved") params.append("isResolved", "false");
    if (selectedLesson) params.append("lessonId", selectedLesson);
    if (selectedTopic) params.append("topicId", selectedTopic);
    const qs = params.toString();
    return `ai/tutor/grouped-deficiencies${qs ? `?${qs}` : ""}`;
  };

  const loadLessons = async () => {
    try {
      const lessonsRes = await ApiService.getLessons(1, 100);
      setLessons(lessonsRes?.data || []);
    } catch (error) {
      console.error("Error loading lessons:", error);
    }
  };

  const loadTopics = async (lessonId: string) => {
    if (!lessonId) {
      setTopics([]);
      return;
    }
    try {
      setTopicsLoading(true);
      const sectionsRes = await ApiService.getLessonSections(lessonId);
      const sectionsList = sectionsRes?.data || sectionsRes || [];
      setTopics(Array.isArray(sectionsList) ? sectionsList.map((s: any) => ({ id: s.id, title: s.title })) : []);
    } catch (error) {
      console.error("Error loading topics:", error);
      setTopics([]);
    } finally {
      setTopicsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedLesson) {
      loadTopics(selectedLesson);
      setSelectedTopic("");
    } else {
      setTopics([]);
      setSelectedTopic("");
    }
  }, [selectedLesson]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const clearFilters = () => {
    setFilterResolved("unresolved");
    setSelectedLesson("");
    setSelectedTopic("");
    setRefreshKey((prev) => prev + 1);
  };

  const handleStartTopicSession = (item: GroupedDeficiency) => {
    window.dispatchEvent(
      new CustomEvent("toggleTopicTutorPanel", {
        detail: {
          contentTitle: item.contentTitle,
          gains: item.gains,
          gainIds: item.gainIds,
          videos: item.videos,
          lesson: item.lesson,
          topic: item.topic,
          onComplete: () => {
            setRefreshKey((prev) => prev + 1);
          },
        },
      })
    );
  };

  const handleOpenAITeacherSupport = async (item: GroupedDeficiency) => {
    const baseContext = {
      gainId: item.gainIds?.[0] || "",
      gainIds: item.gainIds || [],
      gains: item.gains || [],
      gainText: item.gains.map((gain) => gain.gainText).filter(Boolean).join(" | "),
      contentId: item.contentId || "",
      contentTitle: item.contentTitle || "",
      topicId: item.topic?.id || "",
      topicTitle: item.topic?.title || "",
      lessonId: item.lesson?.id || "",
      lessonTitle: item.lesson?.title || "",
      source: "topicDeficiency",
    };

    const gainIds = item.gainIds?.filter(Boolean) || [];
    if (!gainIds.length) {
      window.dispatchEvent(new CustomEvent("toggleAITutorPanel", { detail: { context: baseContext } }));
      return;
    }

    setPlanLoading(item.contentId);

    try {
      // Plan'ı arka planda başlat, sessionPlanId'yi HEMEN al
      const { sessionPlanId } = await ApiService.prepareSessionPlan(gainIds);

      // Bekleme yok — sessionPlanId ile AI tutor'ı hemen aç
      // Backend plan hazır olunca otomatik kullanacak
      setPlanLoading(null);
      window.dispatchEvent(
        new CustomEvent("toggleAITutorPanel", {
          detail: {
            context: { ...baseContext, sessionPlanId },
          },
        })
      );
    } catch {
      setPlanLoading(null);
      window.dispatchEvent(new CustomEvent("toggleAITutorPanel", { detail: { context: baseContext } }));
    }
  };

  const renderFilterActions = () => (
    <div className="flex flex-row flex-wrap gap-2">
      <button
        onClick={() => setFilterOpen((prev) => !prev)}
        className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium border flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 ${filterOpen
            ? "bg-blue-600 text-white hover:bg-blue-700 border-blue-600"
            : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          }`}
        title="Filtrele"
        aria-pressed={filterOpen}>
        <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        <span>Filtrele</span>
      </button>
    </div>
  );

  const renderFilterArea = () => (
    <DeficiencyFilter
      selectedStatus={filterResolved}
      onStatusChange={(status) => {
        setFilterResolved(status);
        setRefreshKey((prev) => prev + 1);
      }}
      selectedLesson={selectedLesson}
      onLessonChange={(lessonId) => {
        setSelectedLesson(lessonId);
        setRefreshKey((prev) => prev + 1);
      }}
      lessons={lessons}
      selectedTopic={selectedTopic}
      onTopicChange={(topicId) => {
        setSelectedTopic(topicId);
        setRefreshKey((prev) => prev + 1);
      }}
      topics={topics}
      topicsLoading={topicsLoading}
      onClearFilters={clearFilters}
    />
  );

  const renderGroupedItem = (item: GroupedDeficiency, currentViewMode?: string) => {
    const totalGains = item.gains.length;
    const unresolvedGains = item.unresolvedCount;
    const isGrid = currentViewMode === "grid";

    return (
      <div
        key={item.contentId}
        className={`bg-white rounded-xl border-2 shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden ${unresolvedGains === 0 ? "border-green-300 bg-green-50/50" : "border-gray-200 hover:border-gray-300"
          } ${isGrid ? "p-3 sm:p-4 h-full" : "p-3 sm:p-4 mb-3"}`}>
        <div className="h-full flex flex-col gap-3 sm:gap-4">

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              {unresolvedGains === 0 ? (
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                </div>
              ) : (
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                </div>
              )}
              <span
                className={`text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-full whitespace-nowrap ${unresolvedGains === 0 ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
                  }`}>
                {unresolvedGains === 0 ? "Tamamlandı" : `${unresolvedGains} Eksik`}
              </span>
            </div>
            <div className="text-xs text-gray-500 font-medium flex items-center gap-1.5 ml-9 sm:ml-10">
              <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" />
              {formatDate(item.createdAt)}
            </div>
          </div>

          <p className="text-gray-900 font-semibold text-sm sm:text-base leading-relaxed break-words">
            {item.contentTitle}
          </p>

          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            {item.lesson && (
              <span className="inline-flex items-center gap-1 sm:gap-1.5 bg-blue-100 text-blue-800 font-medium px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm max-w-full">
                <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="truncate">{item.lesson.title}</span>
              </span>
            )}
            {item.topic && item.topic.title && (
              <span className="inline-flex items-center gap-1 sm:gap-1.5 bg-teal-100 text-teal-800 font-medium px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm max-w-full">
                <Layers className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="truncate">{item.topic.title}</span>
              </span>
            )}
            {item.videos.length > 0 && (
              <span className="inline-flex items-center gap-1 sm:gap-1.5 bg-orange-100 text-orange-800 font-medium px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm whitespace-nowrap">
                <Video className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span>{item.videos.length} Video</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1 sm:gap-1.5 bg-gray-100 text-gray-700 font-medium px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm whitespace-nowrap">
              <BookMarked className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              <span>{totalGains} Kazanım</span>
            </span>
          </div>

          <div className={`mt-auto pt-3 border-t border-gray-200 ${isGrid ? "flex flex-col gap-2" : "flex flex-row flex-wrap gap-2"}`}>

            <button
              onClick={() => {
                setSelectedGainsItem(item);
                setGainsModalOpen(true);
              }}
              className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200 ${isGrid ? "w-full" : "flex-1 min-w-[140px]"
                }`}>
              <Eye className="w-4 h-4" />
              Kazanımları Görüntüle
            </button>

            {unresolvedGains > 0 && (
              <button
                onClick={() => handleStartTopicSession(item)}
                className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-sm ${isGrid ? "w-full" : "flex-1 min-w-[140px]"
                  }`}>
                <Sparkles className="w-4 h-4" />
                İncele & Çöz
              </button>
            )}

            {unresolvedGains > 0 && (
              <button
                onClick={() => handleOpenAITeacherSupport(item)}
                disabled={planLoading === item.contentId}
                className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-orange-700 bg-orange-100 rounded-lg hover:bg-orange-200 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed ${isGrid ? "w-full" : "flex-1 min-w-[140px]"}`}>
                {planLoading === item.contentId ? (
                  <>
                    <Brain className="w-4 h-4 animate-pulse" />
                    Ders planı hazırlanıyor...
                  </>
                ) : (
                  <>
                    <Bot className="w-4 h-4" />
                    AI Öğretmen Destek
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="deficiencies-dynamic-list h-full">
        <DynamicList
          type="grid"
          header={false}
          title="Konu Bazlı Eksikler"
          path={getApiPath()}
          isSearch={false}
          isPagination={true}
          showToggle={true}
          refreshKey={refreshKey}
          actions={renderFilterActions()}
          leftArea={filterOpen ? renderFilterArea : undefined}
          item={renderGroupedItem}
          externalViewMode={viewMode}
          onViewModeChange={setViewMode}
          emptyMessage="Harika! Eksik kazanımınız yok. Tüm kazanımlarınızı tamamladınız veya henüz sınava girmediniz."
          errorMessage="Konu bazlı eksikler yüklenirken bir hata oluştu"
        />
      </div>

      <Modal
        isOpen={gainsModalOpen}
        onClose={() => {
          setGainsModalOpen(false);
          setSelectedGainsItem(null);
        }}
        title="Kazanım Detayları"
        size="lg"
      >
        {selectedGainsItem && (
          <div className="space-y-6">

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 text-lg leading-tight">
                    {selectedGainsItem.contentTitle}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {selectedGainsItem.lesson && (
                      <span className="inline-flex items-center gap-1.5 bg-white text-blue-700 font-medium px-2.5 py-1 rounded-lg text-xs border border-blue-200">
                        <BookOpen className="w-3.5 h-3.5" />
                        {selectedGainsItem.lesson.title}
                      </span>
                    )}
                    {selectedGainsItem.topic && (
                      <span className="inline-flex items-center gap-1.5 bg-white text-teal-700 font-medium px-2.5 py-1 rounded-lg text-xs border border-teal-200">
                        <Layers className="w-3.5 h-3.5" />
                        {selectedGainsItem.topic.title}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="bg-amber-50 rounded-xl p-4 text-center border border-amber-200">
                <p className="text-2xl font-bold text-amber-600">{selectedGainsItem.unresolvedCount}</p>
                <p className="text-xs text-amber-700 font-medium mt-1">Eksik</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center border border-green-200">
                <p className="text-2xl font-bold text-green-600">{selectedGainsItem.resolvedCount}</p>
                <p className="text-xs text-green-700 font-medium mt-1">Tamamlanan</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 text-center border border-gray-200">
                <p className="text-2xl font-bold text-gray-700">{selectedGainsItem.gains.length}</p>
                <p className="text-xs text-gray-600 font-medium mt-1">Toplam</p>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                <BookMarked className="w-4 h-4 text-gray-600" />
                Kazanım Listesi
              </h4>
              <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-2">
                {selectedGainsItem.gains.map((gain, index) => (
                  <div
                    key={gain.id}
                    className={`flex items-start gap-3 p-3 rounded-xl transition-all ${gain.isResolved
                        ? "bg-green-50 border border-green-200"
                        : "bg-amber-50 border border-amber-200"
                      }`}
                  >
                    <span
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${gain.isResolved
                          ? "bg-green-500 text-white"
                          : "bg-amber-500 text-white"
                        }`}
                    >
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-relaxed ${gain.isResolved ? "text-green-800" : "text-gray-700"
                        }`}>
                        {gain.gainText}
                      </p>
                      {(gain.startTime || gain.endTime) && (
                        <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-500">
                          <Play className="w-3 h-3" />
                          <span>Video: {gain.startTime} - {gain.endTime}</span>
                        </div>
                      )}
                    </div>
                    {gain.isResolved ? (
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-100 rounded-lg">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        <span className="text-xs font-medium text-green-700">Tamamlandı</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 rounded-lg">
                        <Clock className="w-4 h-4 text-amber-600" />
                        <span className="text-xs font-medium text-amber-700">Bekliyor</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
